import { useEffect, useRef } from 'react';
import { useRecoilCallback } from 'recoil';
import { useActiveFusionWriter } from '~/hooks/target/useActiveFusionWriter';
import type { Image3D } from '~/domain/patient/image3d';
import type { StructureSet } from '~/domain/patient/structureset';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { DEFAULT_BLEND_SETTINGS } from '~/domain/viewport/fusionBlend';
import { readSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';

/** The datasets an image generation produced, first of each kind first. */
export interface GeneratedDatasets {
    readonly images: ReadonlyArray<Image3D>;
    readonly structureSets: ReadonlyArray<StructureSet>;
}

/**
 * Returns a function that shows generated datasets fused with the image
 * they were generated from: the primary image selected when the function
 * runs, not when it was created. The first generated structure set becomes
 * the active set. When that set is drawn on the first generated image, the
 * generated image becomes the primary and the former primary is fused onto
 * it; otherwise the generated image is fused onto the primary. The fusion
 * goes through the active fusion writer, which tells the user when the
 * pair is refused, and a row that holds the pair shows it at an even
 * blend. With no primary image or no generated image, nothing is fused.
 */
export function useFuseGeneratedImage(): (
    generated: GeneratedDatasets
) => void {
    const { setEnvironmentStructureSet } = useViewportEnvironment();
    const { fuse } = useActiveFusionWriter();
    const latest = useRef({ setEnvironmentStructureSet, fuse });
    useEffect(() => {
        latest.current = { setEnvironmentStructureSet, fuse };
    });

    return useRecoilCallback(
        ({ snapshot }) =>
            (generated: GeneratedDatasets) => {
                const patientId = snapshot
                    .getLoadable(selectedPatientIdState)
                    .getValue();
                const inputImage = readSelectedPrimaryImage(snapshot);
                const generatedImage = generated.images[0];
                const generatedStructureSet = generated.structureSets[0];
                if (!patientId) {
                    return;
                }
                // A refused set switch fuses nothing: the fusion follows the
                // set that was not activated.
                if (
                    generatedStructureSet &&
                    !latest.current.setEnvironmentStructureSet(
                        [generatedStructureSet],
                        patientId
                    )
                ) {
                    return;
                }
                if (!inputImage || !generatedImage) {
                    return;
                }
                const [primary, secondary] =
                    generatedStructureSet?.image === generatedImage
                        ? [generatedImage, inputImage]
                        : [inputImage, generatedImage];
                if (latest.current.fuse(primary, secondary)) {
                    getDatasetEnvironmentRegistry().setBlend(
                        primary.seriesUid,
                        { alpha: DEFAULT_BLEND_SETTINGS.alpha }
                    );
                }
            },
        []
    );
}
